interface SectionEventsItemDateProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  locale?: string;
}

const parseDate = (dateValue?: string | number | Date | null): Date | null => {
  if (!dateValue) return null;
  const parsedDate = new Date(dateValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatWeekday = (date: Date, locale?: string) =>
  new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);

const formatDay = (date: Date, locale?: string) =>
  new Intl.DateTimeFormat(locale, { day: "2-digit" }).format(date);

const formatMonth = (date: Date, locale?: string) =>
  new Intl.DateTimeFormat(locale, { month: "short" }).format(date);

const formatFullDate = (date: Date, locale?: string) =>
  new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

function SectionEventsItemDate({
  startDate,
  endDate,
  locale = "en-GB",
}: Readonly<SectionEventsItemDateProps>) {
  const validStartDate = parseDate(startDate);
  const validEndDate = parseDate(endDate);

  if (!validStartDate) return null;

  const isDateRange =
    !!validEndDate &&
    formatFullDate(validStartDate) !== formatFullDate(validEndDate);

  const isSameMonth =
    !!validEndDate &&
    validStartDate.getFullYear() === validEndDate.getFullYear() &&
    validStartDate.getMonth() === validEndDate.getMonth();

  const renderDateRange = () => (
    <div className="flex flex-col text-center">
      {/* Weekdays */}
      <div className="inline-flex items-center justify-center whitespace-nowrap">
        <span className="t-default-xs">{formatWeekday(validStartDate)}</span>
        <span className="px-1">-</span>
        <span className="t-default-xs">
          {/* biome-ignore lint/style/noNonNullAssertion: <checked in isDateRange> */}
          {formatWeekday(validEndDate!, locale)}
        </span>
      </div>
      {/* Days */}
      <div className="inline-flex items-center justify-center whitespace-nowrap">
        <span className="t-default-lg">
          {formatDay(validStartDate, locale)}
        </span>
        <span className="px-1">-</span>
        {/* biome-ignore lint/style/noNonNullAssertion: <checked in isDateRange> */}
        <span className="t-default-lg">{formatDay(validEndDate!, locale)}</span>
      </div>
      {/* Months */}
      <div className="inline-flex items-center justify-center whitespace-nowrap">
        <span className="t-default-xs">{formatMonth(validStartDate)}</span>
        {!isSameMonth && (
          <>
            <span className="px-1">-</span>
            <span className="t-default-xs">
              {/* biome-ignore lint/style/noNonNullAssertion: <checked in isDateRange> */}
              {formatMonth(validEndDate!, locale)}
            </span>
          </>
        )}
      </div>
    </div>
  );

  const renderSingleDate = () => (
    <div className="flex flex-col text-center">
      <span className="t-default-xs">{formatWeekday(validStartDate)}</span>
      <span className="t-default-lg">{formatDay(validStartDate)}</span>
      <span className="t-default-xs">{formatMonth(validStartDate)}</span>
    </div>
  );

  return (
    <div className="absolute top-2xs right-2xs z-10 flex rounded-md bg-others-white bg-white p-2xs leading-tight shadow-sm">
      {isDateRange ? renderDateRange() : renderSingleDate()}
    </div>
  );
}

export { SectionEventsItemDate };
